/**
 * Staff: the meeting calendar (Meetings) and Settings › Booking, where the firm sets its meeting
 * types and prices, the rules, and each person's hours.
 */
import { MeetingBriefButton } from "@/components/ai-buttons";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CalendarPlus, Copy, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "wouter";
import { LocationNote, money, SlotPicker, timeLabel, whenLabel } from "@/components/booking-ui";
import { ask, askText } from "@/components/dialogs";
import { Empty, Loading, PageHeader, Pager, Section, StatRow, useServerPaged } from "@/components/page";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errorMessage } from "@/lib/api";
import { hasPermission, type Me } from "@/lib/session";
import { toast } from "@/lib/use-toast";
import { cn } from "@/lib/utils";

type MeetingType = { id: string; key: string; name: string; description: string; durationMinutes: number; priceCents: number; businessPriceCents: number; audience: "public" | "clients"; rule: string; includedCount: number; location: "video" | "phone"; staffUserIds: string[]; active: boolean; position: number };
type Hours = { weekday: number; startMinute: number; endMinute: number };
type Setup = {
  settings: { minNoticeHours: number; urgentNoticeHours: number; maxDaysAhead: number; bufferMinutes: number; cancelHours: number; taxSeason: { start: string; end: string }; extensionSeason: { start: string; end: string } | null; textReminders: boolean };
  timeZone: string;
  publicUrl: string;
  paymentsEnabled: boolean;
  rules: Record<string, string>;
  types: MeetingType[];
  staff: Array<{ userId: string; name: string; role: string; hours: Hours[]; meetingLink: string; calendar: { email: string; status: "connected" | "needs_reconnect"; isDemo: boolean } | null }>;
  canManage: boolean;
  googleConfigured: boolean;
  smsConfigured: boolean;
  isDemo: boolean;
  meId: string;
};
type Row = {
  id: string;
  clientId: string | null;
  clientName: string | null;
  prospect: boolean;
  typeName: string;
  location: "video" | "phone";
  staffUserId: string;
  staffName: string;
  startsAt: string;
  endsAt: string;
  status: "pending_payment" | "confirmed" | "completed" | "no_show" | "cancelled";
  charge: "free" | "included" | "pay_now" | "invoice" | "waived";
  priceCents: number;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  note: string;
  meetingUrl: string;
  refundDue: boolean;
  source: string;
};

const select = "h-9 w-full rounded-md border border-input bg-background px-2 text-sm";
const pill = "inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium";
const CHARGE: Record<Row["charge"], string> = { free: "Free", included: "Included", pay_now: "Paid online", invoice: "On next invoice", waived: "Waived" };
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const toTime = (m: number) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const fromTime = (v: string) => {
  const [h, m] = v.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};
const dollars = (cents: number) => (cents / 100).toFixed(2).replace(/\.00$/, "");
const toCents = (v: string) => Math.round(Number(v.replace(/[$,\s]/g, "")) * 100) || 0;

function chargeText(r: Row) {
  return r.priceCents && (r.charge === "invoice" || r.charge === "pay_now") ? `${CHARGE[r.charge]} · ${money(r.priceCents)}` : CHARGE[r.charge];
}

// --- Meetings page ---------------------------------------------------------------------------

export function MeetingsPage({ me }: { me: Me }) {
  const [mine, setMine] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  const [booking, setBooking] = useState(false);
  const sp = useServerPaged(`${mine}|${showCancelled}`);
  const { data, isLoading } = useQuery<{ today: string; timeZone: string; rows: Row[]; total: number; counts: { today: number; week: number; prospects: number; refunds: number } }>({
    queryKey: [`/api/bookings?${mine ? "mine=1&" : ""}${showCancelled ? "cancelled=1&" : ""}offset=${sp.offset}&limit=${sp.size}`],
    placeholderData: (prev) => prev,
  });
  const canEdit = hasPermission(me, "clients.write");
  const canManage = hasPermission(me, "billing.manage");
  const rows = data?.rows ?? [];
  const tz = data?.timeZone ?? "America/New_York";
  const dayOf = (iso: string) => new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso));
  const groups = new Map<string, Row[]>();
  for (const r of rows) groups.set(dayOf(r.startsAt), [...(groups.get(dayOf(r.startsAt)) ?? []), r]);
  const n = data?.counts;
  return (
    <>
      <PageHeader
        title="Meetings"
        description="Every booked meeting: from your public booking page, the client portal, or booked by the team."
        actions={
          canEdit && (
            <Button onClick={() => setBooking(true)}>
              <CalendarPlus className="h-4 w-4" /> Book for a client
            </Button>
          )
        }
      />
      <StatRow
        stats={[
          { label: "Today", value: n?.today ?? 0 },
          { label: "Next 7 days", value: n?.week ?? 0 },
          { label: "With prospects", value: n?.prospects ?? 0, tone: "success" },
          { label: "Refund needed", value: n?.refunds ?? 0, tone: n?.refunds ? "danger" : "default" },
        ]}
      />
      <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} /> Only mine
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={showCancelled} onChange={(e) => setShowCancelled(e.target.checked)} /> Show cancelled
        </label>
      </div>
      {isLoading ? (
        <Loading />
      ) : !rows.length ? (
        <Empty>No meetings in the next eight weeks. Share your booking page from Firm Settings › Client communication › Booking.</Empty>
      ) : (
        <div className="space-y-5">
          {[...groups.entries()].map(([day, list]) => (
            <Section key={day} title={new Intl.DateTimeFormat("en-US", { weekday: "long", month: "long", day: "numeric", timeZone: "UTC" }).format(new Date(`${day}T12:00:00Z`))}>
              <ul className="-m-5 divide-y divide-border">
                {list.map((r) => (
                  <MeetingRow key={r.id} r={r} tz={tz} canEdit={canEdit} canManage={canManage} />
                ))}
              </ul>
            </Section>
          ))}
          <Pager paged={sp.paged(data?.total ?? 0)} noun="meetings" />
        </div>
      )}
      {booking && <BookForClient onClose={() => setBooking(false)} />}
    </>
  );
}

function MeetingRow({ r, tz, canEdit, canManage }: { r: Row; tz: string; canEdit: boolean; canManage: boolean }) {
  const act = useMutation({ meta: { inlineError: true }, mutationFn: ({ path, body }: { path: string; body?: unknown }) => api.send("POST", `/api/bookings/${r.id}/${path}`, body ?? {}) });
  const past = Date.parse(r.startsAt) <= Date.now();
  const status =
    r.status === "cancelled"
      ? ["Cancelled", "bg-muted text-muted-foreground"]
      : r.status === "pending_payment"
        ? ["Awaiting payment", "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200"]
        : r.status === "completed"
          ? ["Held", "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200"]
          : r.status === "no_show"
            ? ["Missed", "bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-200"]
            : ["Confirmed", "bg-primary/10 text-primary"];
  return (
    <li className="flex flex-wrap items-start gap-x-4 gap-y-2 px-4 py-3">
      <span className="w-20 shrink-0 pt-0.5 text-sm font-medium tabular">{timeLabel(r.startsAt, tz)}</span>
      <div className="min-w-0 flex-1 basis-full text-sm sm:basis-0">
        <p>
          <span className="font-medium">{r.typeName}</span>
          {" · "}
          {r.clientId ? (
            <Link href={r.prospect ? `/clients/onboarding/${r.clientId}` : `/clients/${r.clientId}`} className="hover:underline">
              {r.clientName ?? r.contactName}
            </Link>
          ) : (
            r.contactName
          )}
          {r.prospect && <span className={cn(pill, "ml-2 bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200")}>Prospect</span>}
        </p>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span>With {r.staffName}</span>
          <LocationNote location={r.location} durationMinutes={Math.round((Date.parse(r.endsAt) - Date.parse(r.startsAt)) / 60_000)} />
          <span>{chargeText(r)}</span>
          {r.location === "phone" && r.contactPhone && <span>{r.contactPhone}</span>}
        </p>
        {r.note && <p className="mt-1 text-xs text-muted-foreground">“{r.note}”</p>}
        {r.refundDue && <p className="mt-1 text-xs font-medium text-destructive">Paid online but not held: refund it in Stripe, then mark it refunded.</p>}
        {r.location === "video" && !r.meetingUrl && (r.status === "confirmed" || r.status === "pending_payment") && !past && <p className="mt-1 text-xs font-medium text-amber-700 dark:text-amber-300">No video link yet: add one so the client is emailed it.</p>}
        {act.error && <p className="mt-1 text-xs text-destructive">{errorMessage(act.error)}</p>}
      </div>
      <span className={cn(pill, status[1])}>{status[0]}</span>
      {canManage && r.refundDue && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => void ask({ title: `Mark ${money(r.priceCents)} as refunded?`, description: "Do this after you refund the payment in Stripe. The meeting leaves the refund count.", confirmLabel: "Mark refunded" }).then((ok) => ok && act.mutate({ path: "refunded" }))}
        >
          Mark refunded
        </Button>
      )}
      {canEdit && r.status !== "cancelled" && (
        <span className="flex flex-wrap gap-1">
          {r.clientId && r.status === "confirmed" && !past && <MeetingBriefButton bookingId={r.id} title={`${r.typeName} with ${r.clientName ?? r.contactName}`} />}
          {r.meetingUrl && r.status === "confirmed" && !past && (
            <Button size="sm" variant="outline" asChild>
              <a href={r.meetingUrl} target="_blank" rel="noreferrer">
                Join
              </a>
            </Button>
          )}
          {past && r.status === "confirmed" && (
            <>
              <Button size="sm" variant="outline" onClick={() => act.mutate({ path: "outcome", body: { status: "completed" } })}>
                Held
              </Button>
              <Button size="sm" variant="ghost" onClick={() => act.mutate({ path: "outcome", body: { status: "no_show" } })}>
                Missed
              </Button>
            </>
          )}
          {r.location === "video" && (r.status === "confirmed" || r.status === "pending_payment") && !past && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                void askText({
                  title: r.meetingUrl ? "Change the video link" : "Add a video link",
                  description: `${r.contactName} is emailed the link${r.status === "pending_payment" ? " with their confirmation, once paid" : ""}.`,
                  label: "Video meeting link",
                  placeholder: "https://",
                  required: true,
                  defaultValue: r.meetingUrl,
                  validate: (v) => (/^https:\/\/\S+$/.test(v.trim()) ? null : "Paste the full link, starting with https://"),
                  confirmLabel: "Save and email",
                }).then((meetingUrl) => meetingUrl !== null && act.mutate({ path: "link", body: { meetingUrl: meetingUrl.trim() } }))
              }
            >
              {r.meetingUrl ? "Change link" : "Add video link"}
            </Button>
          )}
          {canManage && (r.charge === "invoice" || r.charge === "pay_now") && r.status !== "completed" && r.status !== "no_show" && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => void ask({ title: `Waive the ${money(r.priceCents)} charge?`, description: "No invoice line and no payment for this meeting. It stays booked.", confirmLabel: "Waive charge" }).then((ok) => ok && act.mutate({ path: "waive" }))}
            >
              Waive charge
            </Button>
          )}
          {!past && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                void askText({ title: "Cancel this meeting?", description: `${r.contactName} is emailed. Nothing is owed, and an included meeting is given back.`, label: "Reason (optional, for the team)", confirmLabel: "Cancel meeting", destructive: true }).then(
                  (reason) => reason !== null && act.mutate({ path: "cancel", body: { reason } }),
                )
              }
            >
              Cancel
            </Button>
          )}
        </span>
      )}
    </li>
  );
}

/** Book for any client, or (from a lead) for the one given. */
export function BookForClient({ onClose, preset }: { onClose: () => void; preset?: { id: string; name: string } }) {
  const { data: setup } = useQuery<Setup>({ queryKey: ["/api/booking/setup"] });
  const [q, setQ] = useState("");
  const { data: found } = useQuery<{ items: Array<{ id: string; displayName: string; status: string }> }>({ queryKey: [`/api/clients?limit=20${q ? `&q=${encodeURIComponent(q)}` : ""}`] });
  const [clientId, setClientId] = useState(preset?.id ?? "");
  const [typeId, setTypeId] = useState("");
  const [slot, setSlot] = useState<string | null>(null);
  const [override, setOverride] = useState(false);
  const [at, setAt] = useState("");
  const [staffUserId, setStaffUserId] = useState("");
  const [pay, setPay] = useState<"invoice" | "waived">("invoice");
  const [note, setNote] = useState("");
  const type = setup?.types.find((t) => t.id === typeId);
  const tz = setup?.timeZone ?? "America/New_York";
  // A typed time is wall-clock in the firm's zone.
  const typedInstant = () => {
    if (!at) return null;
    const guess = Date.parse(`${at}:00Z`);
    const offset = Date.parse(new Date(guess).toLocaleString("en-US", { timeZone: tz })) - Date.parse(new Date(guess).toLocaleString("en-US", { timeZone: "UTC" }));
    return new Date(guess - offset).toISOString();
  };
  const startsAt = override ? typedInstant() : slot;
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("POST", "/api/bookings", { clientId, meetingTypeId: typeId, startsAt, pay, note, override, ...(override && staffUserId ? { staffUserId } : {}) }),
    onSuccess: () => {
      toast({ title: "Meeting booked", description: "The client has been emailed a confirmation." });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Book a meeting for a client</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          {preset ? (
            <p>
              For <span className="font-medium">{preset.name}</span>
            </p>
          ) : (
          <div className="space-y-1">
            <Label htmlFor="bf-client">Client or prospect</Label>
            <Input id="bf-client" placeholder="Search by name" value={q} onChange={(e) => setQ(e.target.value)} />
            <select aria-label="Client" className={select} value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">Choose…</option>
              {(found?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName}
                  {c.status === "prospect" ? " (prospect)" : ""}
                </option>
              ))}
            </select>
          </div>
          )}
          <div className="space-y-1">
            <Label htmlFor="bf-type">Meeting</Label>
            <select id="bf-type" className={select} value={typeId} onChange={(e) => (setTypeId(e.target.value), setSlot(null))}>
              <option value="">Choose…</option>
              {(setup?.types ?? [])
                .filter((t) => t.active)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.durationMinutes} min{t.priceCents || t.businessPriceCents ? `, ${money(t.priceCents)} / ${money(t.businessPriceCents)} business` : ""})
                  </option>
                ))}
            </select>
          </div>
          {setup?.canManage && (
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} /> Outside the open hours, or past the client's included meetings
            </label>
          )}
          {type &&
            (override ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="bf-at">Date and time</Label>
                  <Input id="bf-at" type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="bf-staff">With</Label>
                  <select id="bf-staff" className={select} value={staffUserId} onChange={(e) => setStaffUserId(e.target.value)}>
                    <option value="">Anyone free</option>
                    {(setup?.staff ?? []).map((m) => (
                      <option key={m.userId} value={m.userId}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ) : (
              <SlotPicker queryKey={`staff:${type.id}`} timeZone={tz} value={slot} onChange={setSlot} load={(from, to) => api.get<Array<{ startsAt: string }>>(`/api/booking/slots?meetingTypeId=${type.id}&from=${from}&to=${to}`).then((r) => r.map((x) => x.startsAt))} />
            ))}
          {type && (type.priceCents > 0 || type.businessPriceCents > 0) && (
            <fieldset className="space-y-1.5">
              <legend className="mb-1 text-xs text-muted-foreground">If this meeting has a charge</legend>
              <label className="flex items-center gap-2">
                <input type="radio" name="bf-pay" checked={pay === "invoice"} onChange={() => setPay("invoice")} /> Add it to the client's next invoice
              </label>
              {setup?.canManage && (
                <label className="flex items-center gap-2">
                  <input type="radio" name="bf-pay" checked={pay === "waived"} onChange={() => setPay("waived")} /> No charge (waive it)
                </label>
              )}
            </fieldset>
          )}
          <div className="space-y-1">
            <Label htmlFor="bf-note">Note (optional)</Label>
            <Textarea id="bf-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          {startsAt && <p className="text-muted-foreground">{whenLabel(startsAt, tz)}</p>}
          {save.error && <p className="text-destructive">{errorMessage(save.error)}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button onClick={() => save.mutate()} disabled={!clientId || !typeId || !startsAt || save.isPending}>
              Book meeting
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// --- Settings › Booking -----------------------------------------------------------------------

const GOOGLE_RESULT: Record<string, { title: string; description?: string; bad?: boolean }> = {
  connected: { title: "Google Calendar connected", description: "Your busy times are now kept off the booking page, and new meetings get Google Meet links." },
  cancelled: { title: "Google Calendar wasn't connected", description: "The sign-in was cancelled." },
  scopes: { title: "Google Calendar wasn't connected", description: "Allow both calendar permissions on Google's screen, then try again.", bad: true },
  expired: { title: "Google Calendar wasn't connected", description: "That sign-in took too long or was started in another window. Please try again.", bad: true },
  error: { title: "Google Calendar couldn't be connected", description: "Please try again. If it keeps failing, check the Google Cloud setup and the GOOGLE_ secrets.", bad: true },
};

export function BookingSettings({ me }: { me: Me }) {
  const { data, isLoading } = useQuery<Setup>({ queryKey: ["/api/booking/setup"] });
  // Back from Google's sign-in: say how it went, once.
  useEffect(() => {
    const result = new URLSearchParams(window.location.search).get("google");
    const r = result ? GOOGLE_RESULT[result] : undefined;
    if (!r) return;
    toast({ title: r.title, description: r.description, variant: r.bad ? "destructive" : undefined });
    window.history.replaceState(null, "", "/settings?tab=booking");
  }, []);
  const [editing, setEditing] = useState<MeetingType | "new" | null>(null);
  if (isLoading || !data) return <Loading />;
  const people = new Map(data.staff.map((m) => [m.userId, m.name]));
  return (
    <div className="space-y-6">
      <Section title="Your booking page" description="Share this link on your website, Google profile and email signature. New visitors book a discovery call or a consultation without an account.">
        <div className="flex flex-wrap items-center gap-2">
          <code className="min-w-0 break-all rounded-md bg-muted px-2 py-1.5 text-sm">{data.publicUrl}</code>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              void navigator.clipboard.writeText(data.publicUrl);
              toast({ title: "Link copied" });
            }}
          >
            <Copy className="h-4 w-4" /> Copy
          </Button>
          <Button size="sm" variant="ghost" asChild>
            <a href={data.publicUrl} target="_blank" rel="noreferrer">
              Open
            </a>
          </Button>
        </div>
        {!data.paymentsEnabled && <p className="mt-3 text-xs text-muted-foreground">Online card payment isn't set up, so paid meetings are added to the client's next invoice instead.</p>}
      </Section>

      <Section
        title="Meeting types and prices"
        description="Change a price any time; meetings already booked keep the price they were booked at. Owners and admins can waive the charge on any single meeting from Meetings."
        action={
          data.canManage && (
            <Button size="sm" variant="outline" onClick={() => setEditing("new")}>
              <Plus className="h-4 w-4" /> Meeting type
            </Button>
          )
        }
      >
        <ul className="-m-5 divide-y divide-border">
          {data.types.map((t) => (
            <li key={t.id} className={cn("flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-3", !t.active && "opacity-60")}>
              <div className="min-w-0 flex-1 basis-full text-sm sm:basis-0">
                <p className="font-medium">
                  {t.name}
                  {!t.active && <span className="ml-2 text-xs font-normal text-muted-foreground">(off)</span>}
                </p>
                <p className="text-xs text-muted-foreground">
                  {data.rules[t.rule]}
                  {t.includedCount ? ` · ${t.includedCount} included` : ""} · {t.audience === "public" ? "Public page and portal" : "Clients in the portal"} · {t.durationMinutes} min · {t.location === "video" ? "video" : "phone"}
                  {t.staffUserIds.length ? ` · ${t.staffUserIds.map((u) => people.get(u) ?? "—").join(", ")}` : ""}
                </p>
              </div>
              <span className="text-sm font-semibold tabular">
                {t.priceCents || t.businessPriceCents ? (t.priceCents === t.businessPriceCents ? money(t.priceCents) : `${money(t.priceCents)} · ${money(t.businessPriceCents)} business`) : t.rule === "none" || t.rule === "urgent" ? "Free" : "Included"}
              </span>
              {data.canManage && (
                <Button size="sm" variant="ghost" onClick={() => setEditing(t)}>
                  Edit
                </Button>
              )}
            </li>
          ))}
        </ul>
      </Section>

      <Rules setup={data} />
      <Texts setup={data} />
      <Section title="Hours" description={`When each person takes meetings (${data.timeZone.replace("_", " ")} time). Everyone sets their own; owners and admins can set anyone's.`}>
        <div className="space-y-5">
          {data.staff
            .filter((m) => data.canManage || m.userId === me.user?.id)
            .map((m) => (
              <PersonHours key={m.userId} person={m} isMe={m.userId === data.meId} googleConfigured={data.googleConfigured} />
            ))}
        </div>
      </Section>
      {editing && <TypeDialog type={editing === "new" ? null : editing} setup={data} onClose={() => setEditing(null)} />}
    </div>
  );
}

/** Texts to people who ticked the opt-in box: confirmations, a reminder the day before, cancellations. */
function Texts({ setup }: { setup: Setup }) {
  const on = setup.settings.textReminders;
  const ready = setup.smsConfigured || setup.isDemo;
  const save = useMutation({
    mutationFn: (textReminders: boolean) => api.send("PATCH", "/api/booking/settings", { textReminders }),
    onSuccess: (_r, v) => toast({ title: v ? "Text reminders on" : "Text reminders off" }),
  });
  return (
    <Section title="Text reminders" description="A text when a meeting is booked, the day before, and if it's cancelled. Only to people who tick “Text me” when they book; a number that replies STOP is never texted again. No texts between 9 PM and 8 AM.">
      <div className="space-y-3">
        <label className={cn("flex items-start gap-2.5 text-sm", (!setup.canManage || !ready) && "opacity-60")}>
          <input type="checkbox" className="mt-0.5 h-4 w-4 shrink-0" checked={on} disabled={!setup.canManage || !ready || save.isPending} onChange={(e) => save.mutate(e.target.checked)} />
          <span>
            <span className="font-medium">Send text reminders</span>
            <span className="block text-xs text-muted-foreground">{on ? "On: the booking pages show the “Text me” box." : "Off: no texts, and the booking pages don't offer them."}</span>
          </span>
        </label>
        {!ready && <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs dark:border-amber-900 dark:bg-amber-950/40">Texting isn't set up yet. Add TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN and TWILIO_MESSAGING_SERVICE_SID in Replit Secrets, then republish.</p>}
        {setup.isDemo && <p className="text-xs text-muted-foreground">Demo firm: texts are recorded on each client, never sent.</p>}
      </div>
    </Section>
  );
}

function Rules({ setup }: { setup: Setup }) {
  const s = setup.settings;
  const [v, setV] = useState({
    minNoticeHours: String(s.minNoticeHours),
    urgentNoticeHours: String(s.urgentNoticeHours),
    maxDaysAhead: String(s.maxDaysAhead),
    bufferMinutes: String(s.bufferMinutes),
    cancelHours: String(s.cancelHours),
    seasonStart: s.taxSeason.start,
    seasonEnd: s.taxSeason.end,
    extStart: s.extensionSeason?.start ?? "",
    extEnd: s.extensionSeason?.end ?? "",
  });
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () =>
      api.send("PATCH", "/api/booking/settings", {
        minNoticeHours: Number(v.minNoticeHours),
        urgentNoticeHours: Number(v.urgentNoticeHours),
        maxDaysAhead: Number(v.maxDaysAhead),
        bufferMinutes: Number(v.bufferMinutes),
        cancelHours: Number(v.cancelHours),
        taxSeason: { start: v.seasonStart, end: v.seasonEnd },
        extensionSeason: v.extStart && v.extEnd ? { start: v.extStart, end: v.extEnd } : null,
      }),
    onSuccess: () => toast({ title: "Booking rules saved" }),
  });
  const field = (k: keyof typeof v, label: string, placeholder?: string) => (
    <div className="space-y-1">
      <Label htmlFor={`br-${k}`}>{label}</Label>
      <Input id={`br-${k}`} disabled={!setup.canManage} inputMode="numeric" placeholder={placeholder} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} />
    </div>
  );
  return (
    <Section title="Rules" description="Dates are month-day (MM-DD). Tax season calls are offered inside either season; the allowance counts across the year.">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <div className="grid items-end gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {field("minNoticeHours", "Notice needed (hours)")}
          {field("urgentNoticeHours", "Urgent notice (hours)")}
          {field("maxDaysAhead", "Book up to (days ahead)")}
          {field("bufferMinutes", "Gap between meetings (min)")}
          {field("cancelHours", "Free cancel (hours before)")}
        </div>
        <div className="grid gap-3 sm:grid-cols-4">
          {field("seasonStart", "Tax season starts", "01-15")}
          {field("seasonEnd", "Tax season ends", "04-15")}
          {field("extStart", "Extension season starts", "04-16")}
          {field("extEnd", "Extension season ends", "10-15")}
        </div>
        {save.error && <p className="text-sm text-destructive">{errorMessage(save.error)}</p>}
        {setup.canManage && (
          <Button type="submit" size="sm" disabled={save.isPending}>
            Save rules
          </Button>
        )}
      </form>
    </Section>
  );
}

/** A person's Google Calendar: connect (only they can), its status, and disconnect. */
function CalendarLink({ person, isMe, configured }: { person: Setup["staff"][number]; isMe: boolean; configured: boolean }) {
  const cal = person.calendar;
  const connect = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send<{ url: string }>("POST", "/api/google/connect"), onSuccess: (r) => window.location.assign(r.url) });
  const disconnect = useMutation({ mutationFn: () => api.send("POST", "/api/google/disconnect") });
  if (!configured && !cal) return isMe ? <p className="text-xs text-muted-foreground">Google Calendar isn't set up for the firm yet.</p> : null;
  return (
    <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md bg-muted/50 px-3 py-2 text-sm">
      <span className={cn("h-2 w-2 shrink-0 rounded-full", cal?.status === "connected" ? "bg-emerald-500" : cal ? "bg-amber-500" : "bg-muted-foreground/40")} aria-hidden />
      <span className="min-w-0 flex-1">
        {cal?.status === "connected" ? (
          <>
            Google Calendar connected{cal.email ? <> as <span className="font-medium">{cal.email}</span></> : null}
            {cal.isDemo && " (sample calendar)"}
            <span className="block text-xs text-muted-foreground">Busy times there are kept off the booking page; each meeting gets its own Google Meet link.</span>
          </>
        ) : cal ? (
          <>
            Google Calendar needs reconnecting
            <span className="block text-xs text-muted-foreground">Google stopped accepting the connection. Until it's reconnected, bookings use the video link below.</span>
          </>
        ) : (
          <>
            Google Calendar not connected
            <span className="block text-xs text-muted-foreground">{isMe ? "Connect it so your busy times are respected and meetings get Meet links." : "Only they can connect their own calendar."}</span>
          </>
        )}
      </span>
      {isMe && configured && (!cal || cal.status !== "connected") && (
        <Button size="sm" onClick={() => connect.mutate()} disabled={connect.isPending}>
          {cal ? "Reconnect" : "Connect Google Calendar"}
        </Button>
      )}
      {isMe && cal && !cal.isDemo && (
        <Button size="sm" variant="ghost" onClick={() => void ask({ title: "Disconnect Google Calendar?", description: "Meetings already on it stay there. New bookings won't check it or add Meet links.", confirmLabel: "Disconnect" }).then((ok) => ok && disconnect.mutate())}>
          Disconnect
        </Button>
      )}
      {connect.error && <p className="basis-full text-xs text-destructive">{errorMessage(connect.error)}</p>}
    </div>
  );
}

function PersonHours({ person, isMe, googleConfigured }: { person: Setup["staff"][number]; isMe: boolean; googleConfigured: boolean }) {
  const initial = DAYS.map((_, wd) => person.hours.find((h) => h.weekday === wd) ?? null);
  const [days, setDays] = useState(initial.map((h) => ({ on: !!h, start: toTime(h?.startMinute ?? 540), end: toTime(h?.endMinute ?? 1020) })));
  const [link, setLink] = useState(person.meetingLink);
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () =>
      api.send("PUT", `/api/booking/hours/${person.userId}`, {
        hours: days.flatMap((d, wd) => (d.on ? [{ weekday: wd, startMinute: fromTime(d.start), endMinute: fromTime(d.end) }] : [])),
        meetingLink: link.trim(),
      }),
    onSuccess: () => toast({ title: `Hours saved for ${person.name}` }),
  });
  return (
    <div className="rounded-lg border border-border p-4">
      <p className="mb-3 font-medium">{person.name}</p>
      <CalendarLink person={person} isMe={isMe} configured={googleConfigured} />
      {/* Monday first, one row per day. */}
      <div className="grid gap-x-8 gap-y-1.5 lg:grid-cols-2">
        {[1, 2, 3, 4, 5, 6, 0].map((wd) => {
          const d = days[wd]!;
          const set = (patch: Partial<typeof d>) => setDays(days.map((x, i) => (i === wd ? { ...x, ...patch } : x)));
          return (
            <div key={wd} className="flex min-h-9 items-center gap-3 text-sm">
              <label className="flex w-14 shrink-0 items-center gap-1.5">
                <input type="checkbox" checked={d.on} onChange={(e) => set({ on: e.target.checked })} />
                {DAYS[wd]}
              </label>
              {d.on ? (
                <span className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-1.5 sm:max-w-[19rem]">
                  <Input aria-label={`${DAYS[wd]} from`} type="time" step={900} className="h-8 min-w-0 px-2" value={d.start} onChange={(e) => set({ start: e.target.value })} />
                  <span className="text-muted-foreground">to</span>
                  <Input aria-label={`${DAYS[wd]} to`} type="time" step={900} className="h-8 min-w-0 px-2" value={d.end} onChange={(e) => set({ end: e.target.value })} />
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">Not available</span>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex items-end gap-2">
        <div className="min-w-0 flex-1 space-y-1">
          <Label htmlFor={`link-${person.userId}`}>{person.calendar?.status === "connected" ? "Backup video link (used if Google can't be reached)" : "Video meeting link (used until Google Calendar is connected)"}</Label>
          <Input id={`link-${person.userId}`} placeholder="https://meet.google.com/…" value={link} onChange={(e) => setLink(e.target.value)} />
        </div>
        <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
          Save
        </Button>
      </div>
      {save.error && <p className="mt-2 text-sm text-destructive">{errorMessage(save.error)}</p>}
    </div>
  );
}

function TypeDialog({ type, setup, onClose }: { type: MeetingType | null; setup: Setup; onClose: () => void }) {
  const [v, setV] = useState({
    name: type?.name ?? "",
    description: type?.description ?? "",
    durationMinutes: String(type?.durationMinutes ?? 30),
    price: dollars(type?.priceCents ?? 0),
    businessPrice: dollars(type?.businessPriceCents ?? 0),
    audience: type?.audience ?? "public",
    rule: type?.rule ?? "none",
    includedCount: String(type?.includedCount ?? 0),
    location: type?.location ?? "video",
    staffUserIds: type?.staffUserIds ?? [],
    active: type?.active ?? true,
  });
  const body = () => ({
    name: v.name,
    description: v.description,
    durationMinutes: Number(v.durationMinutes),
    priceCents: toCents(v.price),
    businessPriceCents: toCents(v.businessPrice),
    audience: v.audience,
    rule: v.rule,
    includedCount: Number(v.includedCount) || 0,
    location: v.location,
    staffUserIds: v.staffUserIds,
    active: v.active,
  });
  const save = useMutation({ meta: { inlineError: true }, mutationFn: () => (type ? api.send("PATCH", `/api/booking/types/${type.id}`, body()) : api.send("POST", "/api/booking/types", body())), onSuccess: onClose });
  const counted = v.rule === "monthly_plan" || v.rule === "tax_season";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{type ? `Edit ${type.name}` : "New meeting type"}</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3 text-sm"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <div className="space-y-1">
            <Label htmlFor="mt-name">Name</Label>
            <Input id="mt-name" required value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="mt-desc">Description (shown to clients)</Label>
            <Textarea id="mt-desc" rows={2} value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })} />
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor="mt-dur">Minutes</Label>
              <Input id="mt-dur" inputMode="numeric" value={v.durationMinutes} onChange={(e) => setV({ ...v, durationMinutes: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="mt-price">Price, individual ($)</Label>
              <Input id="mt-price" inputMode="decimal" value={v.price} onChange={(e) => setV({ ...v, price: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="mt-bprice">Price, business ($)</Label>
              <Input id="mt-bprice" inputMode="decimal" value={v.businessPrice} onChange={(e) => setV({ ...v, businessPrice: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="mt-rule">Who it's for</Label>
            <select id="mt-rule" className={select} value={v.rule} onChange={(e) => setV({ ...v, rule: e.target.value, audience: ["none", "urgent"].includes(e.target.value) ? v.audience : "clients" })}>
              {Object.entries(setup.rules).map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            {counted && (
              <div className="space-y-1">
                <Label htmlFor="mt-inc">{v.rule === "monthly_plan" ? "Included per month" : "Included per season"}</Label>
                <Input id="mt-inc" inputMode="numeric" value={v.includedCount} onChange={(e) => setV({ ...v, includedCount: e.target.value })} />
              </div>
            )}
            {["none", "urgent"].includes(v.rule) && (
              <div className="space-y-1">
                <Label htmlFor="mt-aud">Bookable on</Label>
                <select id="mt-aud" className={select} value={v.audience} onChange={(e) => setV({ ...v, audience: e.target.value as "public" | "clients" })}>
                  <option value="public">Public page and portal</option>
                  <option value="clients">Client portal only</option>
                </select>
              </div>
            )}
            <div className="space-y-1">
              <Label htmlFor="mt-loc">How</Label>
              <select id="mt-loc" className={select} value={v.location} onChange={(e) => setV({ ...v, location: e.target.value as "video" | "phone" })}>
                <option value="video">Video call</option>
                <option value="phone">Phone call</option>
              </select>
            </div>
          </div>
          <fieldset className="space-y-1.5">
            <legend className="mb-1 text-xs text-muted-foreground">Who takes it (none checked: anyone with hours)</legend>
            <div className="grid gap-1.5 sm:grid-cols-2">
              {setup.staff.map((m) => (
                <label key={m.userId} className="flex items-center gap-2">
                  <input type="checkbox" checked={v.staffUserIds.includes(m.userId)} onChange={(e) => setV({ ...v, staffUserIds: e.target.checked ? [...v.staffUserIds, m.userId] : v.staffUserIds.filter((x) => x !== m.userId) })} />
                  {m.name}
                </label>
              ))}
            </div>
          </fieldset>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={v.active} onChange={(e) => setV({ ...v, active: e.target.checked })} /> Offered (uncheck to stop new bookings)
          </label>
          {save.error && <p className="text-destructive">{errorMessage(save.error)}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              Save
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
